interface KaraokeServicesProps {
  lang: 'en' | 'ja';
}

export default function KaraokeServices({ lang }: KaraokeServicesProps) {
  const content = {
    en: {
      badge: 'What We Do',
      title: 'Full Support for Your Karaoke Experience',
      services: [
        {
          icon: 'ri-calendar-check-line',
          title: 'Pre-Booking',
          description: 'We\'ll reserve the karaoke room for you in advance, so you don\'t have to worry about availability.'
        },
        {
          icon: 'ri-door-open-line',
          title: 'Reception Support',
          description: 'We\'ll handle check-in and all communication with the staff on your behalf.'
        },
        {
          icon: 'ri-remote-control-line',
          title: 'Machine Operation',
          description: 'Don\'t know how to use the karaoke system? We\'ll show you everything step by step.'
        },
        {
          icon: 'ri-playlist-line',
          title: 'Song Recommendations',
          description: 'We\'ll introduce you to popular J-Pop, anime songs, and English tracks that are fun to sing.'
        },
        {
          icon: 'ri-user-heart-line',
          title: 'Join & Enjoy',
          description: 'We\'ll sing along with you and help create a fun, memorable atmosphere.'
        },
        {
          icon: 'ri-question-answer-line',
          title: 'Explanations on Request',
          description: 'We won\'t overwhelm you with information. We\'ll explain things only when you ask.'
        }
      ]
    },
    ja: {
      badge: 'サービス内容',
      title: 'カラオケ体験を完全サポート',
      services: [
        {
          icon: 'ri-calendar-check-line',
          title: '事前予約',
          description: 'カラオケルームを事前に予約しますので、空き状況を心配する必要はありません。'
        },
        {
          icon: 'ri-door-open-line',
          title: '受付サポート',
          description: 'チェックインやスタッフとのやり取りはすべて私たちが代行します。'
        },
        {
          icon: 'ri-remote-control-line',
          title: '機械操作サポート',
          description: 'カラオケシステムの使い方がわからない？すべて段階的にご説明します。'
        },
        {
          icon: 'ri-playlist-line',
          title: '人気曲紹介',
          description: '歌いやすい人気のJ-Pop、アニメソング、英語の曲をご紹介します。'
        },
        {
          icon: 'ri-user-heart-line',
          title: '一緒に盛り上がる',
          description: '一緒に歌って、楽しく思い出に残る雰囲気を作ります。'
        },
        {
          icon: 'ri-question-answer-line',
          title: '解説は聞かれた場合のみ',
          description: '情報で圧倒することはありません。質問があったときだけ説明します。'
        }
      ]
    }
  };

  const t = content[lang];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full mb-6">
            <i className="ri-service-line text-purple-600"></i>
            <span className="text-purple-700 text-sm font-semibold tracking-wide uppercase">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            {t.title}
          </h2>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-white to-purple-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-purple-100 overflow-hidden"
            >
              {/* Decorative Element */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-pink-200/30 to-purple-200/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500"></div>
              
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-6 transition-transform duration-300">
                  <i className={`${service.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}